var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '~/classes/Logger';
import { ENV } from '~/enum/env';
import { api } from '~/services/http/fetchClient';
const requestCache = new Map();
export default function useFetch(endpoint, options = {}) {
    // Destructure options with defaults
    const { autoFetch = true, initialData = undefined, deps = [], onSuccess, onError, transform = data => data, retry = { count: 0, delay: 1000 }, cache = false, customDomain = ENV.ApiEntryPoint, cacheTime = 5 * 60 * 1000, // 5 minutes
     } = options;
    // State for the fetch operation
    const [data, setData] = useState(initialData);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    // Track request state with refs to avoid stale closures
    const isMounted = useRef(true);
    const abortController = useRef(null);
    const retryCount = useRef(0);
    const cacheKey = useRef(endpoint);
    // Clear any ongoing request when component unmounts
    useEffect(() => {
        return () => {
            isMounted.current = false;
            if (abortController.current) {
                abortController.current.abort();
            }
        };
    }, []);
    // Check cache before fetching
    const checkCache = useCallback((key) => {
        if (!cache)
            return null;
        const cachedItem = requestCache.get(key);
        if (cachedItem && Date.now() - cachedItem.timestamp < cacheTime) {
            return cachedItem.data;
        }
        return null;
    }, [cache, cacheTime]);
    // Update cache with new data
    const updateCache = useCallback((key, newData) => {
        if (!cache)
            return;
        requestCache.set(key, {
            data: newData,
            timestamp: Date.now(),
        });
    }, [cache]);
    // Main fetch function
    const fetchData = useCallback((customEndpoint) => __awaiter(this, void 0, void 0, function* () {
        const targetEndpoint = customEndpoint || endpoint;
        cacheKey.current = targetEndpoint;
        // Check cache first
        const cachedData = checkCache(targetEndpoint);
        if (cachedData) {
            if (isMounted.current) {
                const transformedData = transform(cachedData);
                setData(transformedData);
                setStatus('success');
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(transformedData);
            }
            return;
        }
        // Set loading state
        setStatus('loading');
        setError(null);
        try {
            // Create new abort controller for this request
            abortController.current = new AbortController();
            // Make the request
            const response = yield api.get(targetEndpoint, {
                signal: abortController.current.signal,
                customDomain,
            });
            // Transform and set the data
            const transformedData = transform(response);
            if (isMounted.current) {
                setData(transformedData);
                setStatus('success');
                updateCache(targetEndpoint, transformedData);
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(transformedData);
            }
            // Reset retry count on successful request
            retryCount.current = 0;
        }
        catch (error) {
            logger.error('Fetch error:', error);
            // Don't process if component unmounted or request was aborted
            if (!isMounted.current || error.name === 'AbortError')
                return;
            // Handle retry logic
            if (retryCount.current < retry.count) {
                retryCount.current += 1;
                setTimeout(() => {
                    if (isMounted.current)
                        fetchData(targetEndpoint);
                }, retry.delay);
                return;
            }
            // Set error state if retries exhausted
            const errorObject = error instanceof Error ? error : new Error(String(error));
            setError(errorObject);
            setStatus('error');
            onError === null || onError === void 0 ? void 0 : onError(errorObject);
        }
    }), [endpoint, transform, onSuccess, onError, retry, checkCache, updateCache]);
    // Manual refetch function that can target a different endpoint
    const refetch = useCallback((customEndpoint) => {
        fetchData(customEndpoint);
    }, [fetchData]);
    // Refresh the data without changing loading state
    const refresh = useCallback((customEndpoint) => __awaiter(this, void 0, void 0, function* () {
        const targetEndpoint = customEndpoint || endpoint;
        try {
            const response = yield api.get(targetEndpoint, {
                customDomain,
            });
            const transformedData = transform(response);
            if (isMounted.current) {
                setData(transformedData);
                updateCache(targetEndpoint, transformedData);
                onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(transformedData);
            }
            return transformedData;
        }
        catch (error) {
            if (isMounted.current) {
                const errorObject = error instanceof Error ? error : new Error(String(error));
                onError === null || onError === void 0 ? void 0 : onError(errorObject);
            }
            throw error;
        }
    }), [endpoint, transform, onSuccess, onError, updateCache]);
    const cancel = useCallback(() => {
        if (abortController.current) {
            abortController.current.abort();
            setStatus('idle');
        }
    }, []);
    const clearCache = useCallback(() => {
        requestCache.delete(cacheKey.current);
    }, []);
    useEffect(() => {
        if (autoFetch) {
            fetchData();
        }
        return () => {
            // Cleanup function to run when deps change or component unmounts
            if (abortController.current) {
                abortController.current.abort('Component unmounted or dependencies changed');
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoFetch, ...deps]);
    return {
        data,
        error,
        status,
        isLoading: status === 'loading',
        isSuccess: status === 'success',
        isError: status === 'error',
        refetch,
        refresh,
        cancel,
        clearCache,
    };
}
export function clearFetchCache() {
    requestCache.clear();
}
